import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { AreaDeTexto } from './campos';
import { Modal } from './Modal';
import { moeda, percentual } from '../util/formato';

export interface AcordoParaRomper {
  readonly id: string;
  readonly valorTotal: number;
  readonly totalPago: number;
  readonly percentualMulta: number | null;
  readonly seRescindidoHoje: {
    temClausulaPenal: boolean;
    saldoEmAberto: number;
    valorDaMulta: number | null;
    totalExigivel: number;
  };
}

const ID_DE_CONFIRMACAO = 'ROMPER';

/**
 * Rescisão do acordo por inadimplemento.
 *
 * A ficha mostrava "se rescindido hoje, seria exigível X" desde sempre, e não havia
 * botão para rescindir de fato: a rota existia na API e a tela nunca foi construída.
 * Rescindir era, na prática, impossível pelo sistema.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE ESTE DIÁLOGO PEDE UMA PALAVRA DIGITADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Romper não é editar um campo. Numa operação só, o servidor:
 *
 *   · marca o acordo como ROMPIDO — e ele não volta a ATIVO por aqui;
 *   · CANCELA todas as parcelas em aberto;
 *   · tira o devedor da régua de cobrança;
 *   · move o caso para "Encaminhar ao judicial".
 *
 * Um botão "Confirmar" ao lado de "Cancelar" é clicado por engano; digitar a palavra
 * não é. O que já foi PAGO permanece na ficha: o acordo rescindido é a prova da
 * tentativa de composição amigável, e some dela seria perder a prova.
 */
export function ModalRomperAcordo({
  aberto,
  aoFechar,
  acordo,
  devedorId,
  nomeDoDevedor,
}: {
  aberto: boolean;
  aoFechar: () => void;
  acordo: AcordoParaRomper | null;
  devedorId: string;
  nomeDoDevedor: string;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setMotivo('');
    setConfirmacao('');
    setErro(null);
  }, [aberto, acordo]);

  const romper = useMutation({
    mutationFn: () =>
      api.criar(`/acordos/${acordo?.id}/romper`, {
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
      aoFechar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível rescindir o acordo.'),
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    if (confirmacao.trim().toUpperCase() !== ID_DE_CONFIRMACAO) {
      setErro(`Digite ${ID_DE_CONFIRMACAO} para confirmar a rescisão.`);
      return;
    }
    romper.mutate();
  }

  if (!acordo) return null;

  const projecao = acordo.seRescindidoHoje;
  const podeConfirmar = confirmacao.trim().toUpperCase() === ID_DE_CONFIRMACAO;

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Rescindir o acordo por inadimplemento"
      descricao={`${nomeDoDevedor} · acordo de ${moeda(acordo.valorTotal)}`}
      largura="34rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={romper.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={romper.isPending || !podeConfirmar}
          >
            {romper.isPending ? 'Rescindindo…' : 'Rescindir o acordo'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        {/* O NÚMERO PRIMEIRO. É ele que decide se vale rescindir, e ele já estava
            calculado na ficha — aqui aparece detalhado, parcela do que é saldo e
            parcela do que é multa, porque é o que vai para a petição. */}
        <dl className="rounded-md border bg-[color:var(--superficie-afundada)] p-4 text-sm">
          <div className="flex justify-between">
            <dt className="text-[color:var(--texto-suave)]">Saldo em aberto</dt>
            <dd className="tabular">{moeda(projecao.saldoEmAberto)}</dd>
          </div>
          <div className="mt-1.5 flex justify-between">
            <dt className="text-[color:var(--texto-suave)]">
              Cláusula penal
              {acordo.percentualMulta !== null && ` (${percentual(acordo.percentualMulta)})`}
            </dt>
            <dd className="tabular">
              {projecao.valorDaMulta !== null ? (
                moeda(projecao.valorDaMulta)
              ) : (
                <span className="text-[color:var(--texto-tenue)]">não prevista</span>
              )}
            </dd>
          </div>
          <div className="mt-2.5 flex justify-between border-t pt-2.5">
            <dt className="font-medium text-[color:var(--texto)]">Total exigível</dt>
            <dd className="tabular font-semibold" style={{ color: 'var(--acento)' }}>
              {moeda(projecao.totalExigivel)}
            </dd>
          </div>
          {acordo.totalPago > 0 && (
            <p className="mt-3 border-t pt-2.5 text-xs text-[color:var(--texto-tenue)]">
              Os {moeda(acordo.totalPago)} já pagos permanecem na ficha. A multa incide
              apenas sobre o saldo em aberto — cobrá-la sobre o que já foi quitado seria
              enriquecimento sem causa.
            </p>
          )}
        </dl>

        <Alerta tom="atencao">
          Ao rescindir, numa operação só: as parcelas em aberto são{' '}
          <strong>canceladas</strong>, o devedor sai da régua de cobrança e o caso vai
          para <strong>“Encaminhar ao judicial”</strong>. O acordo continua na ficha,
          marcado como rompido — ele é a prova da tentativa de composição amigável.
        </Alerta>

        <AreaDeTexto
          rotulo="Motivo (opcional)"
          valor={motivo}
          aoMudar={setMotivo}
          linhas={2}
          placeholder="Ex.: três parcelas vencidas e sem contato desde 12/08."
        />

        <div>
          <label
            htmlFor={`${ID_DO_FORMULARIO}-confirmacao`}
            className="mb-1 block text-xs font-medium text-[color:var(--texto-suave)]"
          >
            Digite <strong>{ID_DE_CONFIRMACAO}</strong> para confirmar
          </label>
          <input
            id={`${ID_DO_FORMULARIO}-confirmacao`}
            value={confirmacao}
            onChange={(e) => {
              setConfirmacao(e.target.value);
              setErro(null);
            }}
            autoComplete="off"
            className="w-40 rounded-md border bg-[color:var(--superficie)] px-3 py-1.5 text-sm tracking-[0.15em] text-[color:var(--texto)] uppercase focus:border-[color:var(--acento)] focus:outline-none"
          />
          {/* Um "Confirmar" ao lado de "Cancelar" se clica por engano; isto, não. */}
          <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">
            A rescisão não é desfeita por esta tela.
          </p>
        </div>
      </form>
    </Modal>
  );
}
